import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Trash, Plus } from "@phosphor-icons/react";

const STATUS_LABELS = {
  neu: "Neu", bestaetigt: "Bestätigt", anzahlung_erhalten: "Anzahlung erhalten",
  in_arbeit: "In Arbeit", erledigt: "Erledigt", storniert: "Storniert",
};
const CLASS_LABELS = {
  kleinwagen: "Kleinwagen", kompakt: "Kompaktklasse", limousine: "Limousine/Coupé",
  kombi: "Kombi", suv: "SUV", van: "Van/MPV", transporter: "Transporter",
};

export default function AdminTint() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [settings, setSettings] = useState(null);
  const [vps, setVps] = useState([]);
  const [films, setFilms] = useState([]);
  const [newVp, setNewVp] = useState({ make: "", model: "", price: "" });
  const [newFilm, setNewFilm] = useState({ hersteller: "", serie: "", toenungsgrad: "", lichtdurchlaessigkeit: "", abg_nummer: "", garantie: "" });
  const [blockDate, setBlockDate] = useState("");

  const loadAll = async () => {
    try {
      const [b, s, v, f] = await Promise.all([
        api.get("/admin/tint/bookings"), api.get("/admin/tint/settings"),
        api.get("/admin/tint/vehicle-prices"), api.get("/admin/tint/films"),
      ]);
      setBookings(b.data); setSettings(s.data); setVps(v.data); setFilms(f.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin/login");
      else toast.error("Daten konnten nicht geladen werden");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { navigate("/admin/login"); return; }
    loadAll();
  }, [navigate]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/tint/bookings/${id}/status`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status aktualisiert");
    } catch { toast.error("Fehler"); }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await api.put("/admin/tint/settings", patch);
      setSettings(data);
      toast.success("Gespeichert");
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const addVp = async () => {
    if (!newVp.make || !newVp.model || !newVp.price) return toast.error("Bitte Hersteller, Modell und Preis angeben");
    try {
      const { data } = await api.post("/admin/tint/vehicle-prices", { ...newVp, price: Number(newVp.price) });
      setVps([data, ...vps]);
      setNewVp({ make: "", model: "", price: "" });
      toast.success("Sonderpreis angelegt");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const addFilm = async () => {
    if (!newFilm.hersteller || !newFilm.serie) return toast.error("Bitte Hersteller und Serie angeben");
    try {
      const { data } = await api.post("/admin/tint/films", newFilm);
      setFilms([data, ...films]);
      setNewFilm({ hersteller: "", serie: "", toenungsgrad: "", lichtdurchlaessigkeit: "", abg_nummer: "", garantie: "" });
      toast.success("Folie angelegt");
    } catch { toast.error("Fehler"); }
  };

  if (!settings) return <div className="min-h-screen flex items-center justify-center text-slate-500">Lade…</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/admin" className="text-slate-300 hover:text-white flex items-center gap-1.5 text-sm" data-testid="tint-back-dashboard">
            <ArrowLeft size={16} /> Dashboard
          </Link>
          <div className="font-bold">Scheibentönung — Verwaltung</div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="bookings" data-testid="tint-tab-bookings">Buchungen ({bookings.length})</TabsTrigger>
            <TabsTrigger value="prices" data-testid="tint-tab-prices">Preise</TabsTrigger>
            <TabsTrigger value="special" data-testid="tint-tab-special">Sonderpreise</TabsTrigger>
            <TabsTrigger value="films" data-testid="tint-tab-films">Folien</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tint-tab-settings">Termine</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nr.</TableHead><TableHead>Kunde</TableHead><TableHead>Fahrzeug</TableHead>
                    <TableHead>Termin</TableHead><TableHead>Preis</TableHead><TableHead>Anzahlung</TableHead><TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Noch keine Buchungen</TableCell></TableRow>}
                  {bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`tint-booking-row-${b.booking_number}`}>
                      <TableCell className="font-mono font-semibold">{b.booking_number}</TableCell>
                      <TableCell>
                        <div className="font-medium">{b.first_name} {b.last_name}</div>
                        <div className="text-xs text-slate-500">{b.phone} · {b.email}</div>
                      </TableCell>
                      <TableCell>
                        <div>{b.vehicle?.manufacturer} {b.vehicle?.model}</div>
                        <div className="text-xs text-slate-500">{CLASS_LABELS[b.vehicle_class] || b.vehicle_class}{b.license_plate ? ` · ${b.license_plate}` : ""}</div>
                      </TableCell>
                      <TableCell><div className="font-medium">{b.date}</div><div className="text-xs text-slate-500">{b.time} Uhr</div></TableCell>
                      <TableCell className="font-semibold">{b.price?.toLocaleString("de-DE")} €</TableCell>
                      <TableCell>{b.deposit_paid ? <span className="text-emerald-600 font-semibold">✓ bezahlt</span> : <span className="text-slate-400">offen</span>}</TableCell>
                      <TableCell>
                        <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                          <SelectTrigger className="h-9 w-44" data-testid={`tint-status-${b.booking_number}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="prices" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-lg space-y-3">
              <h3 className="font-bold text-slate-900">Festpreise nach Fahrzeugklasse (min. 250 €)</h3>
              {Object.entries(settings.class_prices).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-700">{CLASS_LABELS[k] || k}</span>
                  <Input type="number" defaultValue={v} min={250} className="h-10 w-28"
                    onBlur={(e) => saveSettings({ class_prices: { ...settings.class_prices, [k]: Number(e.target.value) } })}
                    data-testid={`tint-price-${k}`} />
                </div>
              ))}
              <p className="text-xs text-slate-500">Änderungen werden beim Verlassen des Feldes gespeichert.</p>
            </div>
          </TabsContent>

          <TabsContent value="special" className="mt-5 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-end gap-3">
              <div><label className="text-xs font-semibold text-slate-600">Hersteller</label><Input value={newVp.make} onChange={(e) => setNewVp({ ...newVp, make: e.target.value })} className="h-10 w-40" data-testid="vp-make" /></div>
              <div><label className="text-xs font-semibold text-slate-600">Modell</label><Input value={newVp.model} onChange={(e) => setNewVp({ ...newVp, model: e.target.value })} className="h-10 w-40" data-testid="vp-model" /></div>
              <div><label className="text-xs font-semibold text-slate-600">Festpreis €</label><Input type="number" value={newVp.price} onChange={(e) => setNewVp({ ...newVp, price: e.target.value })} className="h-10 w-28" data-testid="vp-price" /></div>
              <Button onClick={addVp} className="h-10 bg-slate-900" data-testid="vp-add"><Plus size={16} className="mr-1" /> Anlegen</Button>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Hersteller</TableHead><TableHead>Modell</TableHead><TableHead>Preis</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {vps.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">Keine Sonderpreise</TableCell></TableRow>}
                  {vps.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{v.make}</TableCell><TableCell>{v.model}</TableCell>
                      <TableCell className="font-semibold">{Number(v.price).toLocaleString("de-DE")} €</TableCell>
                      <TableCell><button onClick={async () => { await api.delete(`/admin/tint/vehicle-prices/${v.id}`); setVps(vps.filter((x) => x.id !== v.id)); }} className="text-red-500 hover:text-red-700"><Trash size={16} /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="films" className="mt-5 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-5 grid sm:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
              {[["hersteller", "Hersteller *"], ["serie", "Serie/Produkt *"], ["toenungsgrad", "Tönungsgrad"], ["lichtdurchlaessigkeit", "Lichtdurchl."], ["abg_nummer", "ABG-Nr."], ["garantie", "Garantie"]].map(([k, l]) => (
                <div key={k}><label className="text-xs font-semibold text-slate-600">{l}</label><Input value={newFilm[k]} onChange={(e) => setNewFilm({ ...newFilm, [k]: e.target.value })} className="h-10" data-testid={`film-${k}`} /></div>
              ))}
              <Button onClick={addFilm} className="h-10 bg-slate-900 sm:col-span-3 lg:col-span-6" data-testid="film-add"><Plus size={16} className="mr-1" /> Folie anlegen</Button>
            </div>
            <p className="text-xs text-slate-500">Wichtig: Nur echte, vom Hersteller bestätigte Werte eintragen — keine ABG-Nummern oder UV-Werte erfinden. Bei ≥ 2 aktiven Folien erscheint die Tönungsgrad-Auswahl im Konfigurator.</p>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Hersteller</TableHead><TableHead>Serie</TableHead><TableHead>Tönung</TableHead><TableHead>ABG</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {films.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">Keine Folien hinterlegt</TableCell></TableRow>}
                  {films.map((f) => (
                    <TableRow key={f.id}>
                      <TableCell>{f.hersteller}</TableCell><TableCell>{f.serie}</TableCell>
                      <TableCell>{f.toenungsgrad || "—"}</TableCell><TableCell>{f.abg_nummer || "—"}</TableCell>
                      <TableCell><button onClick={async () => { await api.delete(`/admin/tint/films/${f.id}`); setFilms(films.filter((x) => x.id !== f.id)); }} className="text-red-500 hover:text-red-700"><Trash size={16} /></button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="mt-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-xl space-y-5">
              <div>
                <h3 className="font-bold text-slate-900">Buchbare Uhrzeiten (nur montags)</h3>
                <Input defaultValue={(settings.slots || []).join(", ")} className="h-10 mt-2" data-testid="tint-slots-input"
                  onBlur={(e) => saveSettings({ slots: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
                <p className="text-xs text-slate-500 mt-1">Kommagetrennt, z.B. 08:00, 09:00, 10:00, 11:00, 12:00, 13:00</p>
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Max. Buchungen pro Slot</h3>
                <Input type="number" min={1} defaultValue={settings.capacity_per_slot} className="h-10 mt-2 w-28" data-testid="tint-capacity-input"
                  onBlur={(e) => saveSettings({ capacity_per_slot: Number(e.target.value) })} />
              </div>
              <div>
                <h3 className="font-bold text-slate-900">Gesperrte Montage (Urlaub / Feiertage)</h3>
                <div className="flex gap-2 mt-2">
                  <Input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} className="h-10" data-testid="tint-block-date" />
                  <Button onClick={() => { if (blockDate) { saveSettings({ blocked_dates: [...(settings.blocked_dates || []), blockDate] }); setBlockDate(""); } }} className="h-10 bg-slate-900" data-testid="tint-block-add">Sperren</Button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(settings.blocked_dates || []).map((d) => (
                    <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-700 text-sm border border-red-100">
                      {d}
                      <button onClick={() => saveSettings({ blocked_dates: settings.blocked_dates.filter((x) => x !== d) })}><Trash size={13} /></button>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
